/**
 * CurrencyInput Component
 * Input component for currency values with automatic formatting
 */

import React, { useState, useEffect, useRef } from 'react';
import { View, TextInput, Text, StyleSheet, ViewStyle } from 'react-native';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';

interface CurrencyInputProps {
  label: string;
  value: number | undefined;
  onChangeValue: (value: number | undefined) => void;
  placeholder?: string;
  helpText?: string;
  error?: string;
  containerStyle?: ViewStyle;
  unit?: string;
}

export default function CurrencyInput({
  label,
  value,
  onChangeValue,
  placeholder = '0',
  helpText,
  error,
  containerStyle,
  unit = '€',
}: CurrencyInputProps) {
  const [displayValue, setDisplayValue] = useState<string>(
    value !== undefined ? formatDisplayValue(value) : ''
  );
  const isUserTypingRef = useRef(false);

  // Sync displayValue when value prop changes from outside (e.g., navigation back)
  useEffect(() => {
    // Only update if user is not currently typing
    if (!isUserTypingRef.current) {
      if (value !== undefined && value !== null) {
        setDisplayValue(formatDisplayValue(value));
      } else {
        setDisplayValue('');
      }
    }
  }, [value]);

  // Format number for display (e.g., "1 500")
  function formatDisplayValue(num: number): string {
    if (num === 0) return '0';
    return num.toLocaleString('fr-FR', {
      maximumFractionDigits: 2,
      useGrouping: true,
    });
  }

  // Parse display string to number
  function parseDisplayValue(text: string): number | undefined {
    // Remove all non-digit characters except comma and dot
    const cleaned = text.replace(/[^\d,\.]/g, '');

    if (cleaned === '' || cleaned === '0') return 0;

    // Replace comma with dot for decimal
    const normalized = cleaned.replace(',', '.');

    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? undefined : parsed;
  }

  const handleChangeText = (text: string) => {
    // Mark that user is actively typing
    isUserTypingRef.current = true;

    // Allow only digits, spaces, commas, and dots
    const sanitized = text.replace(/[^\d\s,\.]/g, '');

    setDisplayValue(sanitized);

    // Parse and notify parent
    const numericValue = parseDisplayValue(sanitized);
    onChangeValue(numericValue);
  };

  const handleBlur = () => {
    // User stopped typing
    isUserTypingRef.current = false;

    // Reformat on blur for clean display
    if (value !== undefined && value !== null) {
      setDisplayValue(formatDisplayValue(value));
    } else {
      setDisplayValue('');
    }
  };

  return (
    <View style={[styles.container, containerStyle]}>
      {label && <Text style={styles.label}>{label}</Text>}

      {helpText && <Text style={styles.helpText}>{helpText}</Text>}

      <View style={styles.inputWrapper}>
        <TextInput
          style={[
            styles.input,
            error && styles.inputError,
          ]}
          value={displayValue}
          onChangeText={handleChangeText}
          onBlur={handleBlur}
          placeholder={placeholder}
          placeholderTextColor={colors.textSecondary}
          keyboardType="numeric"
          returnKeyType="done"
        />
        <Text style={styles.unit}>{unit}</Text>
      </View>

      {error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: spacing.md,
  },
  label: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  helpText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  inputWrapper: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
  },
  input: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    paddingRight: 40, // Space for unit
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    backgroundColor: colors.background,
    minHeight: 52,
  },
  inputError: {
    borderColor: colors.error,
  },
  unit: {
    position: 'absolute',
    right: spacing.md,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textSecondary,
  },
  errorText: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    marginTop: spacing.xs,
  },
});
